import { Location } from '@angular/common';
import { inject } from '@angular/core';
import { forkJoin } from 'rxjs';
import { find, forEach } from 'lodash';
import { MainService } from '../header-entry/services/main.service';
import { AuthenticationService } from '../shared/auth/services/authentication.service';
import { CameraAnnotations } from '../shared/interface/camera-annotations';
import { UserDetails } from '../shared/interface/user-details';
import { ApplicationFeaturesService } from '../shared/services/application-features.service';
import { ApplicationLoginService } from '../shared/services/application-login.service';
import { ApplicationStateService } from '../shared/services/application-state.service';
import { CameraAnnotationService } from '../shared/services/camera-annotation.service';
import { PermissionService } from '../shared/services/permission.service';
import { RabbitMQConfigurationService } from '../shared/services/rabbitmq/services/rabbitmq-configuration.service';
import { ReloadSettingsService } from '../shared/services/reload-settings.service';
import { UserLogInService } from '../shared/services/user-log-in.service';

declare let $: any;
declare let clearGlobalLoaderInterval: any;

// Main initializer registered with provideAppInitializer
export function initializeUserDataFactory(): Promise<boolean> {
  const authenticationService = inject(AuthenticationService);
  const applicationStateService = inject(ApplicationStateService);
  const applicationLoginService = inject(ApplicationLoginService);
  const permissionService = inject(PermissionService);
  const mainService = inject(MainService);
  const reloadSettingsService = inject(ReloadSettingsService);
  const rabbitMQConfigurationService = inject(RabbitMQConfigurationService);
  const userLoginService = inject(UserLogInService);
  const location = inject(Location);
  const applicationFeatureService = inject(ApplicationFeaturesService);
  const cameraAnnotationService = inject(CameraAnnotationService);


  const isLoggedIn = authenticationService.isLoggedIn();
  const userCode = applicationStateService.userCode;
  const currentURL = location.path();
  if(currentURL && currentURL.startsWith('/register')) {
    // if the current URL is /register, then no need to login or check for user login status, just clear the global loader and allow to navigate to registration page
    clearGlobalLoaderTimerInterval();
    return Promise.resolve(true);
  }
  if (!isLoggedIn && userCode && currentURL !== '' && currentURL !== '/login') {
    return loginUser(
      userCode,
      applicationStateService,
      applicationLoginService,
      permissionService,
      mainService,
      reloadSettingsService,
      rabbitMQConfigurationService,
      userLoginService,
      applicationFeatureService,
      cameraAnnotationService,
      clearGlobalLoaderTimerInterval
    );
  } else {
    clearGlobalLoaderTimerInterval();
    if(!userCode && currentURL !== '' && currentURL !== '/login') {
        // if the user directly enters any URL other than the /login, then redirect to login page
        location.go('/login');
    }
    return Promise.resolve(true);
  }
}

function clearGlobalLoaderTimerInterval() {
  if ($('#globalLoaderParent').length) {
    $('#globalLoaderParent').remove();
    clearGlobalLoaderInterval();
  }
}

function loginUser(
  userCode: string,
  applicationStateService: ApplicationStateService,
  applicationLoginService: ApplicationLoginService,
  permissionService: PermissionService,
  mainService: MainService,
  reloadSettingsService: ReloadSettingsService,
  rabbitMQConfigurationService: RabbitMQConfigurationService,
  userLoginService: UserLogInService,
  applicationFeatureService: ApplicationFeaturesService,
  cameraAnnotationService: CameraAnnotationService,
  clearGlobalLoaderTimerInterval: () => void
): Promise<boolean> {
  return new Promise<boolean>((resolve, reject) => {
    const customerCredential = {
      Password: userCode,
      ExternalPassword: '',
      Username: ''
    };
    applicationStateService.isAuthenticated = true;

    applicationLoginService
      .getCustomer(customerCredential)
      .subscribe({
        next: (response: any) => {
          if (!response) {
            userLoginService.dataSubject.next(Promise.resolve(false));
            clearGlobalLoaderTimerInterval();
            return reject(false);
          }

          const userDetails: UserDetails = response.UserTerminalDetails.UserDetails;
          const terminals = response.UserTerminalDetails.TerminalSummary;

          setTerminalDetails(terminals, applicationStateService);

          if (!userDetails) {
            userLoginService.dataSubject.next(Promise.resolve(false));
            clearGlobalLoaderTimerInterval();
            return reject(false);
          }

          loginCompleted(
            userDetails,
            applicationStateService,
            permissionService,
            mainService,
            reloadSettingsService,
            rabbitMQConfigurationService,
            userLoginService,
            applicationLoginService,
            applicationFeatureService,
            cameraAnnotationService,
            clearGlobalLoaderTimerInterval
          )
            .then(() => resolve(true))
            .catch((error) => reject(error));
        },
        error: (error) => {
          userLoginService.dataSubject.next(Promise.resolve(false));
          clearGlobalLoaderTimerInterval();
          reject(error);
        }
      });
  });
}

function loginCompleted(
  userDetails: UserDetails,
  applicationStateService: ApplicationStateService,
  permissionService: PermissionService,
  mainService: MainService,
  reloadSettingsService: ReloadSettingsService,
  rabbitMQConfigurationService: RabbitMQConfigurationService,
  userLoginService: UserLogInService,
  applicationLoginService: ApplicationLoginService,
  applicationFeatureService: ApplicationFeaturesService,
  cameraAnnotationService: CameraAnnotationService,
  clearGlobalLoaderTimerInterval: () => void
): Promise<boolean> {
  return new Promise<boolean>((resolve, reject) => {
    const loginObservables = [
      permissionService.getUserPermission(applicationStateService.impersonateUserDetails?.id),
      mainService.getApplicationVersion(),
      mainService.initializeApplication(),
      reloadSettingsService.reloadSettings(applicationStateService.terminalId ?? 0),
      mainService.getConfigurationsForWebApp(),
      mainService.getApplicationInitalizationConfiguration(),
      applicationFeatureService.getApplicationFeatures(),
      cameraAnnotationService.getActiveCameraAnnotation()
    ];

    forkJoin(loginObservables)
      .subscribe({
        next: (response: any[]) => {
          setSettingsToLocalStorage(userDetails, applicationStateService);

          const permissionData = response[0] ? response[0] : null;
          const appVersion = response[1]?.CurrentVersion ?? null;
          const licenseInfo = response[2]?.LicenseInfo ?? null;
          const cameraAnnotationsResponse: Array<CameraAnnotations> = response[7];

          forEach(cameraAnnotationsResponse, (annotations: CameraAnnotations) => {
            applicationStateService.cameraAnnotations[annotations.KeyName] = annotations.Annotation;
          });

          applicationStateService.applicationInitializationConfigurations = appVersion;
          applicationStateService.userPermissions = permissionData;
          applicationStateService.licenseInfo = licenseInfo;
          applicationStateService.settingParam = response[3];
          applicationStateService.setLocalizationSettings(applicationStateService.settingParam);

          const configResponse = response[4];
          const applicationConfigResponse = response[5];

          if (configResponse) {
            applicationStateService.configurations =
              rabbitMQConfigurationService.initializeConfigurationsObject(configResponse);

            getUserPreference(applicationLoginService, applicationStateService);

            rabbitMQConfigurationService.setConfig();
            rabbitMQConfigurationService.subscribeConnectionState();

            document.title = configResponse.CustomerName;
          }

          if (applicationConfigResponse) {
            applicationStateService.applicationInitializationConfigurations = applicationConfigResponse;
          }

          userLoginService.dataSubject.next(Promise.resolve(true));
          clearGlobalLoaderTimerInterval();
          resolve(true);
        },
        error: (error) => {
          userLoginService.dataSubject.next(Promise.resolve(false));
          clearGlobalLoaderTimerInterval();
          reject(error);
        }
      });
  });
}

function setTerminalDetails(terminals: any[], applicationStateService: ApplicationStateService) {
  if (!terminals) return;
  const terminalDetails = find(terminals, (terminal: any) => {
    return terminal.TerminalId === applicationStateService.terminalId;
  });
  if (terminalDetails) {
    applicationStateService.terminalName = terminalDetails.TerminalName;
    applicationStateService.terminalType = terminalDetails.TerminalType;
    applicationStateService.cookieTerminalName = terminalDetails.TerminalName;
  }
}

function setSettingsToLocalStorage(userDetails: UserDetails, applicationStateService: ApplicationStateService): void {
  if (!userDetails) return;
  applicationStateService.userId = userDetails.id;
  applicationStateService.cookieUserId = userDetails.id;
  applicationStateService.userDetails = userDetails;
  applicationStateService.sessionApplicationMode = 'application';
}

function getUserPreference(
  applicationLoginService: ApplicationLoginService,
  applicationStateService: ApplicationStateService
) {
  applicationLoginService.getUserPreference(applicationStateService.userId).subscribe({
    next: (response) => {
      applicationLoginService.setUserPreferences(response?.UserPreferenceModel);
    }
  });
}
